import FUTURE_FAIR_LOGO from '../assets/images/future_fair.png';
import FUTURE_FAIR_FAVICON from '../assets/images/future_fair_favicon.png';

export { FUTURE_FAIR_LOGO, FUTURE_FAIR_FAVICON };

/**
 * @enum {string}
 */
export const UserType = {
  ADMIN: 'ADMIN',
  SUPERADMIN: 'SUPERADMIN',
};

export const MEDIUM_TAGS = [
  'All Art',
  'Paintings',
  'Videos',
  'Multimedia',
  'Photography',
  'Sculptures',
  'Drawings',
  'Installation',
];

export const TOP_TAGS = [
  'Black Experience',
  'Still Life',
  'Pop Art',
  'Street Art',
  'Cartoons',
  'Sexuality',
  'Feminism',
  'Material Experimentation',
  'Mysticism',
  'Portraits',
  'Realism',
  'Nature',
  'Societal Critique',
  'Food',
  'Architecture',
  'Collage',
  'Animals',
  'Found Objects',
  'Geometry',
  'Immigrant',
  'Interior Spaces',
  'Motherhood',
  'Surrealism',
  'The Gaze',
  'Mental Health',
  'LGBTQ',
  'Gender Identity',
  'Otherworldly',
  'Environmentalism',
  'Political Commentary',
  'Mythology',
  'Papier-mâché',
];

export const STYLE_TAGS = ['Figurative', 'Conceptual', 'Landscape', 'Abstract'];

export const TYPE_OPTIONS = ['Gallery', 'Museum', 'Fair Demo'];

export const FUTURE_FAIR_SHOW_TYPE = 'Future Fair';

export const ON_VIEW_TAGS = ['On View', 'Opening Soon', 'Closing Soon', 'Opening Today'];

export const LOCATION_NY_TAGS = [
  'Chelsea',
  'Tribeca',
  'Lower East Side',
  'Lower Manhattan',
  'SoHo-NoHo',
  'West Village',
  'East Village',
  'Murray Hill',
  'Midtown',
  'Upper East Side',
  'Upper Manhattan West',
  'North Brooklyn',
  'Central Brooklyn',
  'South Brooklyn',
  'Queens',
  'Bronx',
];

export const LOCATION_LA_TAGS = [
  'Downtown LA',
  'Hollywood',
  'Central LA',
  'Westside',
  'Coastal',
  'South LA',
  'Northeast LA',
  'Long Beach',
  'Pasadena',
  'Greater LA',
];

// For backwards compatibility
export const LOCATION_TAGS = LOCATION_NY_TAGS;

export const INIT_MAP_VIEW_STATE = {
  longitude: parseFloat('-74.004'),
  latitude: parseFloat('40.72'),
  zoom: parseFloat('14'),
};

export const INIT_MAP_VIEW_STATE_LA = {
  longitude: parseFloat('-118.3508'),
  latitude: parseFloat('34.07630'),
  zoom: parseFloat('12'),
};

export const INIT_MAP_VIEW_STATE_BOG = {
  longitude: parseFloat('-74.0721'),
  latitude: parseFloat('4.7109'),
  zoom: parseFloat('12'),
};

export const LOCATION_BOG_TAGS = [
  'Las Nieves',
  'Centro Historico',
  'La Candelaria',
  'Universidad Nacional',
  'San Felipe',
  'Quinta Camacho',
  'La Macarena',
  'Zona Rosa',
  'Chicó',
  'Chapinero',
  'Teusaquillo',
  'La Soleda',
  'Galerias',
];

export const CITY_OPTIONS = [
  { value: 'New York', label: 'NYC' },
  { value: 'Los Angeles', label: 'LA' },
  { value: 'Bogotá', label: 'BOG' },
];

export const cityLabel = city => CITY_OPTIONS.find(o => o.value === city)?.label ?? city;

const CITY_TAG_SUFFIX = { 'New York': 'NY', 'Los Angeles': 'LA', Bogotá: 'BOG' };
export const cityTagSuffix = city => CITY_TAG_SUFFIX[city] ?? 'NY';

export const getLocationTagsForCity = city => {
  if (city === 'Los Angeles') return LOCATION_LA_TAGS;
  if (city === 'Bogotá') return LOCATION_BOG_TAGS;
  return LOCATION_NY_TAGS;
};

export function bannerMatchesCity(banner, city) {
  const cities = banner.city;
  if (cities == null || (Array.isArray(cities) && cities.length === 0)) return true;
  if (typeof cities === 'string') return cities === city;
  return cities.includes(city);
}

export const getInitMapViewStateForCity = city => {
  if (city === 'Los Angeles') return INIT_MAP_VIEW_STATE_LA;
  if (city === 'Bogotá') return INIT_MAP_VIEW_STATE_BOG;
  return INIT_MAP_VIEW_STATE;
};

export const trendingShowsLayer = {
  id: 'trendingShowsLayer',
  type: 'symbol',
  source: 'my-data',
  filter: ['boolean', ['get', 'recommended'], true],
  layout: {
    'icon-image': 'pin-icon',
    'icon-size': 1,
    'icon-allow-overlap': true,
  },
  paint: {
    'icon-color': '#FF8C00',
  },
};

export const regularShowsLayer = {
  id: 'regularShowsLayer',
  type: 'symbol',
  source: 'my-data',
  filter: ['==', ['get', 'recommended'], false],
  layout: {
    'icon-image': 'pin-icon-reg',
    'icon-size': 1,
    'icon-allow-overlap': true,
  },
};

export const formatDate = (dateString, showYear) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  if (showYear) {
    return `${monthNames[month - 1]} ${day}, ${year}`;
  }
  return `${monthNames[month - 1]} ${day}`;
};

export const formatDateRange = (startDateString, endDateString) => {
  if (!startDateString) return '';
  if (!endDateString) return formatDate(startDateString, true);

  const startYear = startDateString.split('-')[0];
  const endYear = endDateString.split('-')[0];
  const sameYear = startYear === endYear;

  return `${formatDate(startDateString, !sameYear)} – ${formatDate(endDateString, true)}`;
};

/**
 * Function to return a boolean if the end dates is lower than
 * today's date
 */
export function isPastShow(endDate) {
  const today = new Date().setHours(0, 0, 0, 0);
  const [year, month, day] = endDate.split('-');
  const end = new Date(year, month - 1, day).setHours(0, 0, 0, 0);
  return end < today;
}

const BOOTH_SUFFIX_RE = /\s*\(Booth [^)]*\)\s*$/i;

/**
 * The title the feed shows: the artist's name for a solo show, otherwise the show title.
 * Fair shows (previous display title ends in "(Booth …)") keep their curated title as-is,
 * since it's usually the gallery name rather than the artist.
 * @param {string|null} showTitle
 * @param {string[]} artistNames
 * @param {string|null} [prevDisplayTitle]
 * @returns {string}
 */
export function buildDisplayTitle(showTitle, artistNames, prevDisplayTitle) {
  if (prevDisplayTitle && BOOTH_SUFFIX_RE.test(prevDisplayTitle)) return prevDisplayTitle;
  if (artistNames?.length === 1) return artistNames[0];
  return showTitle?.trim() || 'Various Artists';
}

/**
 * Splits shows into the dashboard's Live / Past / Hidden groups.
 * @param {Array} shows
 * @returns {{ live: Array, past: Array, hidden: Array }}
 */
export function groupShowsByStatus(shows) {
  const groups = { live: [], past: [], hidden: [] };
  shows.forEach(show => {
    if (show.is_archived) {
      if (!show.merged_into) groups.hidden.push(show);
    } else if (isPastShow(show.end_date)) {
      groups.past.push(show);
    } else {
      groups.live.push(show);
    }
  });
  return groups;
}

/**
 * Function to convert a single time string to 12-hour format with AM/PM
 */
export function formatTime(timeString) {
  const [hour, minute] = timeString.split(':');
  let hourInt = parseInt(hour, 10);
  const period = hourInt >= 12 ? 'p.m.' : 'a.m.';
  hourInt = hourInt % 12 || 12; // Convert to 12-hour format
  return `${hourInt}:${minute} ${period}`;
}

/**
 * Function to format the start and end times
 * @param {*} startTime
 * @param {*} endTime
 * @returns
 */
export function formatTimeRange(startTime, endTime) {
  if (!startTime) {
    return '';
  }
  const formattedStartTime = formatTime(startTime);
  if (!endTime) {
    return formattedStartTime;
  }
  const formattedEndTime = formatTime(endTime);
  return `${formattedStartTime} – ${formattedEndTime}`;
}

/**
 * Function to format a date string to YYYY-MM-DD format
 * @param {*} date
 * @returns
 */
export function formatDateToYYYYMMDD(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format search string to be used in textSearch supabase query.
 * This function replaces all spaces with a plus sign (+)
 * to create a search string that can be used in the textSearch query.
 * @param searchString - the search string
 * @returns the formatted search string for Postgres tsquery
 */
export function formatSearchStringForFts(searchString) {
  return searchString.includes(' ') ? searchString.split(' ').join('+') : searchString;
}

/**
 * Sanitize a filename to only contain safe characters.
 */
export function sanitizeFileName(fileName) {
  const lastDot = fileName.lastIndexOf('.');
  const name = lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
  const ext = lastDot > 0 ? fileName.slice(lastDot) : '';
  const safeName = name.replace(/[^a-zA-Z0-9_-]/g, '_');
  const safeExt = ext.replace(/[^a-zA-Z0-9.]/g, '');
  return `${safeName}${safeExt}`;
}

/**
 * Returns a friendly readable joined date string,
 * e.g "Joined in Jan 2020"
 * @param {*} dateString
 * @returns
 */
export function formatJoinDate(dateString) {
  const date = new Date(dateString);
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${month} ${year}`;
}

/**
 * Build daily counts for a contribution-style heatmap.
 * @param {{ created_at: string }[]} rows
 * @param {number} [monthsBack=6]
 * @returns {{ data: Record<string, number>, startDate: Date, endDate: Date }}
 */
export function buildHeatmapData(rows, monthsBack = 6) {
  const now = new Date();
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const startMonth = now.getMonth() - (monthsBack - 1);
  const startDate = new Date(now.getFullYear(), startMonth, 1);

  const dateCounts = {};
  rows.forEach(r => {
    if (!r.created_at) return;
    const d = new Date(r.created_at.replace(' ', 'T'));
    if (d >= startDate && d <= endDate) {
      const iso = d.toISOString().split('T')[0];
      dateCounts[iso] = (dateCounts[iso] || 0) + 1;
    }
  });

  const normalizedDateCounts = {};
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const iso = d.toISOString().split('T')[0];
    normalizedDateCounts[iso] = dateCounts[iso] || 0;
  }

  return { data: normalizedDateCounts, startDate, endDate };
}

/**
 * Bucket raw show_views rows into a time-series array suitable for Recharts.
 *
 * @param {{ created_at: string }[]} rows   - raw rows from show_views
 * @param {'day'|'week'|'month'} granularity
 * @param {Date} fromDate                   - start of the window
 * @param {Date} [toDate]                   - end of the window (default: now)
 * @returns {{ date: string, views: number }[]}
 */

const NY_TIME_ZONE = 'America/New_York';

export function nyCalendarDate(date) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: NY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const { year, month, day } = Object.fromEntries(parts.map(p => [p.type, Number(p.value)]));
  return new Date(Date.UTC(year, month - 1, day));
}

function dayKey(d) {
  return d.toISOString().split('T')[0];
}

function weekKey(d) {
  const sunday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  sunday.setUTCDate(sunday.getUTCDate() - sunday.getUTCDay()); // rewind to Sunday
  return dayKey(sunday);
}

function monthKey(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function bucketKeyForDate(d, granularity) {
  const keyFor = granularity === 'day' ? dayKey : granularity === 'week' ? weekKey : monthKey;
  return keyFor(d);
}

function createEmptyBuckets(granularity, fromDate, toDate) {
  const buckets = {};

  if (granularity === 'day') {
    const cursor = new Date(Date.UTC(fromDate.getUTCFullYear(), fromDate.getUTCMonth(), fromDate.getUTCDate()));
    while (cursor <= toDate) {
      buckets[dayKey(cursor)] = 0;
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
  } else if (granularity === 'week') {
    const cursor = new Date(Date.UTC(fromDate.getUTCFullYear(), fromDate.getUTCMonth(), fromDate.getUTCDate()));
    cursor.setUTCDate(cursor.getUTCDate() - cursor.getUTCDay()); // rewind to Sunday
    while (cursor <= toDate) {
      buckets[dayKey(cursor)] = 0;
      cursor.setUTCDate(cursor.getUTCDate() + 7);
    }
  } else {
    const cursor = new Date(Date.UTC(fromDate.getUTCFullYear(), fromDate.getUTCMonth(), 1));
    while (cursor <= toDate) {
      buckets[monthKey(cursor)] = 0;
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
  }

  return buckets;
}

/**
 * Empty date-range skeleton shared by aggregateByGranularity and aggregateImpressionsByGranularity.
 * @param {'day'|'week'|'month'} granularity
 * @param {Date} fromDate
 * @param {Date} [toDate]
 */
export function createBucketSkeleton(granularity, fromDate, toDate = nyCalendarDate(new Date())) {
  return createEmptyBuckets(granularity, fromDate, toDate);
}

function aggregateRowsIntoBuckets(rows, granularity, buckets, extract) {
  const filled = { ...buckets };
  const keyFor = granularity === 'day' ? dayKey : granularity === 'week' ? weekKey : monthKey;

  rows.forEach(row => {
    const entry = extract(row);
    if (!entry) return;
    const [date, amount] = entry;
    const key = keyFor(date);
    if (key in filled) filled[key] += amount;
  });

  return Object.entries(filled)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, views]) => ({ date, views }));
}

export function aggregateByGranularity(rows, granularity, fromDate, toDate = nyCalendarDate(new Date()), buckets = createEmptyBuckets(granularity, fromDate, toDate)) {
  return aggregateRowsIntoBuckets(rows, granularity, buckets, ({ created_at }) => created_at ? [nyCalendarDate(new Date(created_at)), 1] : null);
}

/**
 * Bucket pre-aggregated show_impressions rows into a time-series array.
 * @param {{ view_date: string, count: number }[]} rows
 * @param {'day'|'week'|'month'} granularity
 * @param {Date} fromDate
 * @param {Date} [toDate]
 * @param {Record<string, number>} [buckets]
 * @returns {{ date: string, views: number }[]}
 */
export function aggregateImpressionsByGranularity(rows, granularity, fromDate, toDate = nyCalendarDate(new Date()), buckets = createEmptyBuckets(granularity, fromDate, toDate)) {
  return aggregateRowsIntoBuckets(rows, granularity, buckets, ({ view_date, count }) => view_date ? [new Date(view_date), count ?? 0] : null);
}

/**
 * An object to map fair names to their respective logos
 */
/**
 * Generate embed URL with current filter state
 */
export const generateEmbedUrl = (filters, baseUrl = window.location.origin) => {
  const params = new URLSearchParams();

  if (filters.open?.size) {
    params.set('open', Array.from(filters.open).join(','));
  }
  if (filters.sortedByRelevance) {
    params.set('sortedByRelevance', 'true');
  }
  if (filters.selectedMediums?.size) {
    params.set('mediums', Array.from(filters.selectedMediums).join(','));
  }
  if (filters.selectedLocation?.size) {
    params.set('locations', Array.from(filters.selectedLocation).join(','));
  }
  if (filters.selectedSubject?.size) {
    params.set('subjects', Array.from(filters.selectedSubject).join(','));
  }
  if (filters.selectedStyles?.size) {
    params.set('styles', Array.from(filters.selectedStyles).join(','));
  }
  if (filters.query) {
    params.set('query', filters.query);
  }
  if (filters.showType?.size) {
    params.set('types', Array.from(filters.showType).join(','));
  }
  if (filters.fairName) {
    params.set('fairName', filters.fairName);
  }

  const queryString = params.toString();
  return `${baseUrl}/embed-map${queryString ? `?${queryString}` : ''}`;
};

/**
 * Generate iframe embed code
 */
export const generateEmbedCode = (embedUrl, width = '100%', height = '600px') => {
  return `<iframe
  src="${embedUrl}"
  width="${width}"
  height="${height}"
  frameborder="0"
  style="border:0;"
  allowfullscreen=""
  aria-hidden="false"
  tabindex="0">
</iframe>`;
};
